import {
  Box,
  Button,
  Card,
  Divider,
  Grid,
  List,
  ListItem,
  Skeleton,
  Stack,
  Typography,
} from "@mui/material";
import {
  getFormattedStatsCount,
  handleErrorMessage,
  isTruthy,
} from "helpers/methods";
import { useState, useEffect, useContext } from "react";
import upgradePlanStyles from "./UpgradePlan.styles";
import { CustomSwitch } from "screens/Settings/Users/ViewUser";
import { getCustomPlan, getNewPlans } from "../PlanService";
import { MyPlanContext } from "../Context";
import { features } from "../AccountUsage/AccountUsage";
import ContactLimitSlider from "./ContactLimitSlider";
import { isEmpty } from "lodash";
import Icon from "assets/icons1/TopNavIcons/DomainsIcon.svg";

interface CustomProps {
  upgradePlan: boolean;
  setUpgradePlan: Function;
}

interface planLimits {
  entity: string;
  limit: number;
}

const UpgradePlan = (props: CustomProps) => {
  const classes = upgradePlanStyles;
  const { setUpgradePlan } = props;
  const { setSelectedPlan, featurePlanDetails, accountPlan, isActivePlan } =
    useContext(MyPlanContext);
  const currentSubscribers =
    accountPlan?.appPlanLimitList?.find(
      (limit: planLimits) => limit.entity === "contact"
    )?.current ?? 0;
  const [isLoading, setIsLoading] = useState(false);
  const [planCurrency, setPlanCurrency] = useState("USD");
  const [planType, setPlanType] = useState("monthly");
  const [plans, setPlans] = useState<any>(null);
  const [customPlan, setCustomPlan] = useState<any>([]);
  const [contacts, setContacts] = useState(currentSubscribers);
  const [currentPricing, setCurrentPricing] = useState<any>(null);
  const activePlanId = featurePlanDetails.id;

  useEffect(() => {
    fetchCustomPlan();
    fetchNewPlans();
  }, []);

  useEffect(() => {
    setContacts(currentSubscribers);
  }, [accountPlan, planCurrency]);

  useEffect(() => {
    if (!plans || !plans[planCurrency]) return;

    const availableContactCounts = Object.keys(plans[planCurrency])
      .map(Number)
      .sort((a, b) => a - b);

    const selectedContactCount =
      availableContactCounts.find((count) => count === contacts) ??
      availableContactCounts.find((count) => count > contacts) ??
      availableContactCounts[availableContactCounts.length - 1];

    if (selectedContactCount !== contacts) {
      setContacts(selectedContactCount);
    } else {
      setCurrentPricing(plans[planCurrency][selectedContactCount]);
    }
  }, [plans, planCurrency, contacts]);

  useEffect(() => {
    if (plans && plans[planCurrency] && contacts in plans[planCurrency]) {
      setCurrentPricing(plans[planCurrency][contacts]);
    }
  }, [planType]);

  const fetchNewPlans = async () => {
    try {
      setIsLoading(true);
      const response = await getNewPlans();
      setPlans(response);
      setIsLoading(false);
    } catch (error: any) {
      setIsLoading(false);
      handleErrorMessage(error);
    }
  };

  const fetchCustomPlan = async () => {
    try {
      setIsLoading(true);
      const response = await getCustomPlan();
      setCustomPlan(response);
      setIsLoading(false);
    } catch (error: any) {
      setIsLoading(false);
      handleErrorMessage(error);
    }
  };

  const newPlans = () => [
    {
      id:
        planType === "annually"
          ? currentPricing?.Standard?.Yearly?.id
          : currentPricing?.Standard?.Monthly?.id,
      name: "Standard",
      price:
        planType === "annually"
          ? currentPricing?.Standard?.Yearly?.price
          : currentPricing?.Standard?.Monthly?.price,
      emails:
        planType === "annually"
          ? currentPricing?.Standard?.Yearly?.metadata.find(
              (meta: any) => meta.name === "Emails"
            )?.value
          : currentPricing?.Standard?.Monthly?.metadata.find(
              (meta: any) => meta.name === "Emails"
            )?.value,
    },
    {
      id:
        planType === "annually"
          ? currentPricing?.Premium?.Yearly?.id
          : currentPricing?.Premium?.Monthly?.id,
      name: "Premium",
      price:
        planType === "annually"
          ? currentPricing?.Premium?.Yearly?.price
          : currentPricing?.Premium?.Monthly?.price,
      emails:
        planType === "annually"
          ? currentPricing?.Premium?.Yearly?.metadata?.find(
              (meta: any) => meta.name === "Emails"
            )?.value
          : currentPricing?.Premium?.Monthly?.metadata?.find(
              (meta: any) => meta.name === "Emails"
            )?.value,
    },
    {
      id: customPlan.id ?? "",
      name: "Custom",
      price: customPlan.price ?? 0,
      emails:
        customPlan?.metadata?.find((meta: any) => meta.name === "Emails")
          ?.value ?? 0,
      contacts:
        customPlan?.metadata?.find((meta: any) => meta.name === "Contacts")
          ?.value ?? 0,
      currency: customPlan?.currency ?? "USD",
    },
  ];

  const handlePlanTypeToggle = (event: any) => {
    const newPlanType = event.target.checked ? "annually" : "monthly";
    setPlanType(newPlanType);
  };

  const getUpgradePlanHeader = () => {
    return (
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{
          border: "1px solid #47556940",
          borderRadius: "8px",
          p: 2,
        }}
      >
        <Stack>
          <Box>
            <Typography variant="h6" color="text.primary" fontWeight={500}>
              Current contacts:{" "}
              <Typography component="span" variant="h4">
                {currentSubscribers.toLocaleString()}
              </Typography>
            </Typography>
          </Box>
        </Stack>

        <Stack direction="row" alignItems="center">
          <Typography
            variant="body2"
            fontWeight={600}
            pr={1}
            color="text.primary"
          >
            Billed annually
          </Typography>
          <CustomSwitch
            checked={planType === "annually"}
            onChange={handlePlanTypeToggle}
          />
          <Typography
            variant="body2"
            fontWeight={600}
            pl={1}
            color={planType === "annually" ? "#7551D0" : "text.primary"}
          >
            (~10% in yearly plan)
          </Typography>
        </Stack>
      </Stack>
    );
  };

  const getPlanPrice = (plan: any) => {
    if (plan?.name === "Custom" && !isTruthy(customPlan)) {
      return "Let’s Connect";
    } else {
      return planCurrency === "INR" || plan?.currency === "INR"
        ? `₹${getFormattedStatsCount(plan?.price ?? 0)}`
        : `$${getFormattedStatsCount(plan?.price ?? 0)}`;
    }
  };

  const getChoosePlanText = (plan: any) => {
    if (isActivePlan && activePlanId === plan.id) {
      return "Your Current Plan";
    } else if (plan.name === "Custom" && !isTruthy(customPlan)) {
      return "Contact Us";
    } else {
      return "Choose Plan";
    }
  };

  const getChoosePlanHandler = (plan: any) => {
    if (plan.name === "Custom" && !isTruthy(customPlan)) {
      return () => {
        window.open("https://calendly.com/mailzzy", "_blank");
      };
    } else {
      return () => {
        setUpgradePlan(true);
        setSelectedPlan(plan);
      };
    }
  };

  const getUpgradePlan = (plan: any) => {
    return (
      <Grid item xs={12} md={4}>
        <Card sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
          <Box sx={classes.planPriceBox}>
            <Stack direction="row" spacing={1}>
              <Stack direction="row">
                <Typography variant="caption" sx={classes.planPrice}>
                  {plan.name}
                </Typography>
              </Stack>
            </Stack>
          </Box>
          <Stack p={3} pb={0} flexGrow={1}>
            <Stack direction="column" mb={2}>
              <Stack direction="row" spacing={1} pb={2}>
                <Stack direction="row">
                  <Typography variant="caption" sx={classes.planPrice}>
                    {getPlanPrice(plan)}
                  </Typography>
                </Stack>
                {(plan.name !== "Custom" ||
                  (plan.name === "Custom" && isTruthy(customPlan))) && (
                  <Typography sx={classes.planPriceText} variant="caption">
                    / month
                  </Typography>
                )}
              </Stack>
              {planType === "annually" && plan.name !== "Custom" && (
                <Stack direction="row" spacing={2} pb={1}>
                  <Typography variant="body2" color="text.secondary">
                    {`$${(plan.price * 12).toFixed(2)} billed yearly`}
                  </Typography>
                </Stack>
              )}
              <Divider sx={{ mb: 2 }} />
              {plan?.name === "Custom" && !isTruthy(customPlan) && (
                <Typography
                  variant="body2"
                  component="span"
                  sx={{ color: "text.secondary", textTransform: "capitalize" }}
                >
                  More contacts, more emails, or special requirements? We’ll
                  create a custom plan that works for your business.
                </Typography>
              )}
              {(plan.name !== "Custom" ||
                (plan.name === "Custom" && isTruthy(customPlan))) && (
                <>
                  <Stack pl={3} pb={1} spacing={2}>
                    <List sx={{ listStyleType: "disc", p: "0px !important" }}>
                      <ListItem
                        sx={{ display: "list-item", p: "0px !important" }}
                      >
                        <Typography
                          variant="body2"
                          component="span"
                          sx={{ textTransform: "capitalize" }}
                          fontWeight={600}
                        >
                          {plan.name === "Custom" && customPlan
                            ? Number(plan.contacts)?.toLocaleString()
                            : contacts.toLocaleString()}
                          {" Contacts"}
                        </Typography>
                      </ListItem>
                    </List>
                  </Stack>
                  <Stack pl={3} pb={1} spacing={2}>
                    <List sx={{ listStyleType: "disc", p: "0px !important" }}>
                      <ListItem
                        sx={{ display: "list-item", p: "0px !important" }}
                      >
                        <Typography
                          variant="body2"
                          component="span"
                          sx={{ textTransform: "capitalize" }}
                          fontWeight={600}
                        >
                          {Number(plan?.emails)?.toLocaleString()}
                          {" Emails / Month"}
                        </Typography>
                      </ListItem>
                    </List>
                  </Stack>
                </>
              )}
            </Stack>
            <Stack>
              {features
                .filter(
                  (category) =>
                    category.title === "Email" ||
                    category.title === "Marketing CRM"
                )
                .map((category, index) => (
                  <Box key={index} style={{ marginBottom: "16px" }}>
                    <Typography
                      variant="subtitle1"
                      sx={{ fontWeight: 600, marginBottom: 1 }}
                    >
                      {category.title}
                    </Typography>
                    {category.items.map((item, i) => (
                      <Typography
                        key={i}
                        variant="body2"
                        sx={{
                          color: "text.secondary",
                          marginBottom: 0.5,
                        }}
                      >
                        {item}
                      </Typography>
                    ))}
                  </Box>
                ))}
            </Stack>
          </Stack>
          <Box
            display="flex"
            justifyContent="center"
            sx={{ mt: "auto", mb: 2 }}
          >
            <Button
              variant="contained"
              disabled={isActivePlan && activePlanId === plan.id}
              onClick={getChoosePlanHandler(plan)}
            >
              {getChoosePlanText(plan)}
            </Button>
          </Box>
        </Card>
      </Grid>
    );
  };

  const getSkeleton = () => {
    return (
      <Grid container spacing={2}>
        {[1, 2, 3].map((_, index) => (
          <Grid item xs={12} sm={4} key={index}>
            <Skeleton animation="wave" variant="rounded" height={700} />
          </Grid>
        ))}
      </Grid>
    );
  };
  const getUpgradePlans = () => {
    return (
      <Stack direction="column" spacing={2}>
        {getUpgradePlanHeader()}
        {isTruthy(featurePlanDetails) && isTruthy(plans) && (
          <ContactLimitSlider
            marks={Object.keys(plans[planCurrency]).map((key) => ({
              value: Number(key),
            }))}
            contacts={contacts}
            setContacts={setContacts}
            currency={planCurrency}
            setCurrency={setPlanCurrency}
            currencies={Object.keys(plans)}
            currentSubscribers={currentSubscribers}
          />
        )}
        <Box>
          <Grid container spacing={3} alignItems="stretch">
            {isTruthy(featurePlanDetails) &&
              isTruthy(plans) &&
              newPlans().map((plan: any) => getUpgradePlan(plan))}
          </Grid>
          <Stack pt={2} direction="row" alignItems="center">
            <Box
              display="flex"
              flexDirection="row"
              alignItems="center"
              component={"a"}
              href="https://mailzzy.com/pricing/"
              target="_blank"
              sx={{
                textDecoration: "none",
              }}
            >
              <Typography color="text.primary">
                View feature comparison on the website
              </Typography>
              <img src={Icon} alt="Icon" />
            </Box>
          </Stack>
        </Box>
      </Stack>
    );
  };

  return (
    <>
      {isLoading || isEmpty(featurePlanDetails)
        ? getSkeleton()
        : getUpgradePlans()}
    </>
  );
};

export default UpgradePlan;
